import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import Chip from '@/components/General/Chip';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import SheetModal from '@/components/General/SheetModal';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';
import { getTaskPurposeLabel, getTaskPurposeIcon } from '@/utils/constants/followUp';
import { showAlert } from '@/utils/showAlert';

/**
 * FollowUpPersonTasks — the person's care-task list on the Follow Up care
 * surface (spec §6.1/§10). Open work first; cancelled rows are labelled and
 * never shown as completed. Rows link to the shared task occurrence screen —
 * the same TaskInstance everywhere (§1.3.7).
 *
 * Assign/cancel controls only render for 'Manage Follow Up' holders or the
 * person's primary caregiver; the server enforces regardless.
 */
const FollowUpPersonTasks = ({ organizationID, personID, canManage, isPrimaryCaregiver }) => {
    const router = useRouter();
    const { data: tasks = [] } = Query.followUp.personTasks({ organizationID, personID });
    const canAdminister = canManage || isPrimaryCaregiver;

    const [assigningTask, setAssigningTask] = useState(null);
    const [cancellingTask, setCancellingTask] = useState(null);
    const [cancelReason, setCancelReason] = useState('');

    const assignMutation = Mutation.followUp.assignTask({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Task assigned' });
            setAssigningTask(null);
        },
        onError: (error) => {
            if (!error?.expected) Toast.show({ type: 'error', text2: 'Failed to assign the task.' });
        },
    });

    const cancelMutation = Mutation.followUp.cancelTask({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Task cancelled' });
            setCancellingTask(null);
            setCancelReason('');
        },
        onError: (error) => {
            if (!error?.expected) Toast.show({ type: 'error', text2: 'Failed to cancel the task.' });
        },
    });

    if (tasks.length === 0) return null;

    const open = tasks.filter((t) => !t.completed && !t.cancelledAt);
    const closed = tasks.filter((t) => t.completed || t.cancelledAt);

    const rowOptions = (task) => {
        if (!canAdminister) return [];
        const options = [{
            name: 'Assign to…',
            icon: 'person-add-outline',
            onSelect: () => setAssigningTask(task),
        }];
        if (!task.completed && !task.cancelledAt) {
            options.push({
                name: 'Cancel Task',
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => showAlert({
                    title: 'Cancel Task',
                    subtitle: `Cancel "${task.Task?.title || 'this task'}"? Open assignees will be notified it is no longer needed.`,
                    isDestructive: true,
                    confirmText: 'Cancel Task',
                    onConfirm: () => {
                        setCancelReason('');
                        setCancellingTask(task);
                    },
                }),
            });
        }
        return options;
    };

    const renderTask = (task) => {
        const purpose = task.followUpContext?.purpose;
        const due = task.dueDate ? new Date(task.dueDate) : null;
        const status = task.cancelledAt ? 'Cancelled' : task.completed ? 'Completed' : 'Open';
        const options = rowOptions(task);
        const assigneeNames = (task.Assignees || [])
            .map((a) => `${a.firstName || ''} ${a.lastName || ''}`.trim())
            .filter(Boolean);

        return (
            <Pressable
                key={task.id}
                testID={`follow-up-task-${task.id}`}
                accessibilityRole="button"
                onPress={() => router.push(`/tasks/instances/${task.id}`)}
                style={[styles.taskRow, status !== 'Open' && styles.taskRowClosed]}
            >
                <View style={styles.taskIcon}>
                    <Unicon name={getTaskPurposeIcon(purpose)} size={16} colorVariant="paths" />
                </View>
                <View style={styles.taskBody}>
                    <Text style={styles.taskTitle} numberOfLines={1}>{task.Task?.title || getTaskPurposeLabel(purpose)}</Text>
                    <Text style={styles.taskMeta} numberOfLines={1}>
                        {getTaskPurposeLabel(purpose)}
                        {due ? ` · Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                        {assigneeNames.length ? ` · ${assigneeNames.join(', ')}` : ' · Unassigned'}
                    </Text>
                </View>
                {status !== 'Open' && (
                    <Chip
                        testID={`follow-up-task-status-${task.id}`}
                        icon={status === 'Cancelled' ? 'close-circle-outline' : 'checkmark-circle-outline'}
                        colorVariant={status === 'Cancelled' ? 'textSecondary' : 'success'}
                    >
                        {status}
                    </Chip>
                )}
                {options.length > 0 && <DropdownMenu testID={`follow-up-task-menu-${task.id}`} options={options} />}
                <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
            </Pressable>
        );
    };

    return (
        <View style={styles.section} testID="follow-up-person-tasks">
            <Text style={styles.sectionTitle}>Tasks</Text>
            <View style={styles.card}>
                {open.map(renderTask)}
                {closed.map(renderTask)}
            </View>

            {/* Assign picker — single person via follow_up_assign_task. */}
            <SheetSelector
                type="people"
                title={assigningTask ? `Assign "${assigningTask.Task?.title || 'task'}" to` : 'Assign to'}
                open={!!assigningTask}
                onOpen={() => {}}
                onClose={() => setAssigningTask(null)}
                value={null}
                excludeIds={personID ? [personID] : []}
                onChange={(selected) => {
                    if (!selected?.id || !assigningTask?.id) return;
                    assignMutation.mutate({
                        organizationID,
                        taskInstanceID: assigningTask.id,
                        assigneePersonID: selected.id,
                        personID,
                    });
                }}
            >
                <View />
            </SheetSelector>

            {/* Cancel reason */}
            <SheetModal
                isOpen={!!cancellingTask}
                onClose={() => setCancellingTask(null)}
                title="Cancel Task"
                subtitle={cancellingTask?.Task?.title || undefined}
                stayOpenOnBlur
                snapPoints={[260, 420]}
                loading={cancelMutation.isPending}
                confirmText="Cancel Task"
                onConfirm={() => {
                    cancelMutation.mutate({
                        organizationID,
                        taskInstanceID: cancellingTask?.id,
                        reason: cancelReason.trim() || null,
                        personID,
                    });
                }}
            >
                <InputSection>
                    <Input
                        title="Reason (optional)"
                        helpText="Shared with managers — keep it brief."
                        value={cancelReason}
                        onUpdate={setCancelReason}
                    />
                </InputSection>
            </SheetModal>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    section: { gap: 6 },
    sectionTitle: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        boxShadow: shadows.card,
        overflow: 'hidden',
    },
    taskRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    taskRowClosed: { opacity: 0.65 },
    taskIcon: {
        width: 30,
        height: 30,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    taskBody: { flex: 1, minWidth: 0 },
    taskTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    taskMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
}));

export default FollowUpPersonTasks;
